import { Link, useParams } from 'react-router-dom';
import { Callout, KeyValues } from '@nyce/admin-kit/console';
import { db, type DbServerRef } from '../../api/db';
import { Card, EmptyState, ErrorBox, Loading, Page, TableScroller, useAsync } from '../../components/common';
import { formatBytes, formatCount, formatDateTime } from '../../lib/format';
import { serverName, useSortedServers } from '../../pages/databases/shared';
import {
  ADVISOR_PATH,
  indexAdvisor,
  reviewError,
  reviewOf,
  type Candidate,
  type CandidateHit,
} from '../api/indexAdvisor';
import {
  CoverageChip,
  ExistingIndexes,
  ExpandButton,
  formatShare,
  Statements,
  TableStatsView,
  useOpenRows,
  VerdictChip,
  ReplicaOf,
} from '../shared';
import '../indexadvisor.css';

/** One candidate of a scan (`/databases/index-advisor/:scanId/:candidateId`): review, evidence, databases. */
export function CandidatePage() {
  const { scanId = '', candidateId = '' } = useParams();
  const scan = scanId === 'latest' ? 'latest' : Number(scanId);
  const data = useAsync(() => indexAdvisor.candidate(scan, candidateId), [scanId, candidateId]);
  const serverList = useAsync(() => db.servers(), []);
  const servers = useSortedServers(serverList.data);
  const c = data.data?.candidate;
  const back = `${ADVISOR_PATH}${data.data ? `?scan=${data.data.scan.id}` : ''}`;
  return (
    <Page
      title={
        <span className="iax-crumbs">
          <Link to={back} className="nyk-back">
            Index advisor
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          {c ? c.tableName : 'Candidate'}
        </span>
      }
      description={
        data.data ? `Scan #${data.data.scan.id}, ${formatDateTime(data.data.scan.createdAt)}` : undefined
      }
    >
      <ErrorBox error={data.error} />
      <Loading loading={data.loading && !data.data} />
      {c && data.data && (
        <>
          <div className="iax-grid">
            <IndexCard c={c} />
            <ReviewCard c={c} />
          </div>
          <Card title="Statements" hint="Normalized; the databases whose advisor tied them to this index.">
            <Statements statements={c.evidence.statements} databases />
          </Card>
          <div className="iax-grid">
            <Card title="Table" hint="Medians over the databases recommending it.">
              {c.evidence.table ? (
                <KeyValues
                  rows={[
                    [
                      'Rows',
                      `${formatCount(c.evidence.table.rowsMedian)} (max ${formatCount(c.evidence.table.rowsMax)})`,
                    ],
                    ['Size (max)', formatBytes(c.evidence.table.bytesMax)],
                    [
                      'Writes',
                      `${formatCount(c.evidence.table.insertsMedian)} ins · ${formatCount(c.evidence.table.updatesMedian)} upd · ${formatCount(c.evidence.table.deletesMedian)} del`,
                    ],
                    [
                      'Scans',
                      `${formatCount(c.evidence.table.seqScansMedian)} seq · ${formatCount(c.evidence.table.idxScansMedian)} index`,
                    ],
                  ]}
                />
              ) : (
                <p className="nyk-ui-hint">No table statistics.</p>
              )}
            </Card>
            <Card title="Indexes the tenants have" hint="On this table, with how many databases have each.">
              {c.evidence.existingIndexes.length ? (
                <TableScroller>
                  <table className="nyk-ui-table nyk-cards" aria-label="Existing indexes">
                    <thead>
                      <tr>
                        <th>Index</th>
                        <th className="nyk-ui-num">Databases</th>
                        <th className="nyk-ui-num">Scans (median)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.evidence.existingIndexes.map((x) => (
                        <tr key={x.name + x.def}>
                          <td className="nyk-card-title">
                            <b className="nyk-wrap">{x.name}</b>
                            <div className="iax-ddl iax-muted">{x.def}</div>
                          </td>
                          <td data-label="Databases" className="nyk-ui-num">
                            {formatCount(x.databases)}
                          </td>
                          <td data-label="Scans (median)" className="nyk-ui-num">
                            <span className={x.scansMedian === 0 ? 'iax-unused' : undefined}>
                              {formatCount(x.scansMedian)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroller>
              ) : (
                <p className="nyk-ui-hint">No indexes on this table.</p>
              )}
            </Card>
          </div>
          <Hits hits={data.data.hits} servers={servers} />
        </>
      )}
      {data.data && !c && <EmptyState card title="No such candidate." />}
    </Page>
  );
}

function IndexCard({ c }: { c: Candidate }) {
  return (
    <Card title="Index">
      <pre className="iax-sql-block" data-testid="iax-candidate-ddl">
        {c.ddl}
      </pre>
      <KeyValues
        rows={[
          ['Table', c.tableName],
          [
            'Recommended by',
            `${formatCount(c.databases)} of ${formatCount(c.evidence.withAdvisor)} databases (${formatShare(c.evidence.share)})`,
          ],
          ['Default set', <CoverageChip key="c" coverage={c.coverage} coveredBy={c.coveredBy} />],
          [
            'Key',
            <span key="k" className="iax-ddl">
              {c.key}
            </span>,
          ],
        ]}
      />
    </Card>
  );
}

function ReviewCard({ c }: { c: Candidate }) {
  const r = reviewOf(c);
  const err = reviewError(c);
  return (
    <Card title="Review">
      {r ? (
        <div className="iax-detail" data-testid="iax-review">
          <VerdictChip review={r} />
          {r.ddl && (
            <>
              <pre className="iax-sql-block">{r.ddl}</pre>
              {r.ddlInvalid && (
                <Callout tone="warning" title="The proposed DDL did not parse">
                  It is not used in the recommendation.
                </Callout>
              )}
            </>
          )}
          <KeyValues
            rows={[
              ['Class', r.class === 'none' ? null : r.class],
              ['When', r.when],
              ['Why', r.why],
              ['Cost', r.cost],
              ['Model', r.model ? <span className="nyk-mono">{r.model}</span> : null],
              ['At', r.at ? formatDateTime(r.at) : null],
            ]}
          />
        </div>
      ) : err ? (
        <Callout tone="error" title="The review of this table failed">
          {err}
        </Callout>
      ) : (
        <p className="nyk-ui-hint">Not reviewed. Review the scan on the index advisor page.</p>
      )}
    </Card>
  );
}

function Hits({ hits, servers }: { hits: CandidateHit[]; servers: DbServerRef[] }) {
  const rows = useOpenRows();
  return (
    <Card
      title={`Databases (${new Set(hits.map((h) => `${h.replicaOf ?? h.serverId}/${h.database}`)).size})`}
      hint="Each database whose advisor recommends this index; a read replica's copy counts as its primary's."
    >
      {!hits.length ? (
        <p className="nyk-ui-hint">No databases.</p>
      ) : (
        <TableScroller>
          <table
            className="nyk-ui-table nyk-cards"
            aria-label="Databases recommending it"
            data-testid="iax-hits"
          >
            <thead>
              <tr>
                <th>Database</th>
                <th>Server</th>
                <th className="nyk-ui-num">Rows</th>
                <th className="nyk-ui-num">Statements</th>
                <th />
              </tr>
            </thead>
            {hits.map((h) => {
              const id = `${h.serverId}/${h.database}`;
              const open = rows.isOpen(id);
              return (
                <tbody key={id} className="nyk-row-group" data-hit={id}>
                  <tr>
                    <td className="nyk-card-title nyk-wrap">
                      <Link
                        to={`/databases?tab=advice&server=${encodeURIComponent(h.serverId)}&db=${encodeURIComponent(h.database)}`}
                      >
                        {h.database}
                      </Link>
                    </td>
                    <td data-label="Server" className="nyk-wrap">
                      {serverName(servers, h.serverId)}
                      <ReplicaOf servers={servers} primary={h.replicaOf} />
                    </td>
                    <td data-label="Rows" className="nyk-ui-num">
                      {h.tableStats ? formatCount(h.tableStats.rows) : '—'}
                    </td>
                    <td data-label="Statements" className="nyk-ui-num">
                      {formatCount(h.statements.length)}
                    </td>
                    <td className="nyk-row-actions">
                      <ExpandButton open={open} onToggle={() => rows.toggle(id)}>
                        Details
                      </ExpandButton>
                    </td>
                  </tr>
                  {open && (
                    <tr className="iax-detail-row">
                      <td colSpan={5}>
                        <div className="iax-detail">
                          <p className="nyk-ui-hint">
                            Advisor: <span className="iax-ddl">{h.recommended}</span>
                          </p>
                          <h3>Statements</h3>
                          <Statements statements={h.statements} />
                          {h.tableStats && (
                            <>
                              <h3>Table</h3>
                              <TableStatsView stats={h.tableStats} />
                            </>
                          )}
                          <h3>Indexes</h3>
                          <ExistingIndexes indexes={h.tableIndexes} label={`Indexes in ${h.database}`} />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </TableScroller>
      )}
    </Card>
  );
}
